import { createColumnHelper } from "@tanstack/react-table";

import {
  emptyString,
  tableColumns,
  featureFlagsEnums,
  socialLinkPlatformEnum,
  reactSelectStylingConst,
  STYLES,
} from "../../constants";
import { StatusIndicator } from "../../../../shared";

export const isFeatureActive = (features, featureId) =>
  features?.includes(featureId);

export const customFeatureStyle = {
  option: (baseStyles, state) => ({
    ...baseStyles,
    color: state.isDisabled
      ? reactSelectStylingConst.grayColor
      : reactSelectStylingConst.blackColor,
    backgroundColor: state.isSelected
      ? reactSelectStylingConst.transparentColor
      : STYLES.AUTO,
  }),
};

const columnHelper = createColumnHelper();

export const googleSocialLinkColumns = (
  GoogleSocialLinkComponent,
  GoogleSocialLinkActionComponent
) => {
  return [
    columnHelper.accessor("name", {
      header: () => "STORE",
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("email", {
      header: () => "EMAIL",
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("phone", {
      header: () => "PHONE",
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("googleSocialLink", {
      header: () => "GOOGLE SOCIAL LINK",
      footer: (info) => info.column.id,
      cell: GoogleSocialLinkComponent,
    }),
    columnHelper.accessor("action", {
      header: () => "ACTION",
      enableSorting: false,
      cell: GoogleSocialLinkActionComponent,
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("status", {
      header: () => "STATUS",
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <StatusIndicator isActive={original?.googleSocialLink} />
      ),
      footer: (info) => info.column.id,
    }),
  ];
};

export const featureFlagsColumns = (FeatureFlagActionComponent) => {
  return [
    {
      name: <span className="fw-bold">BRAND</span>,
      selector: (row) => row.brandName,
      width: `${tableColumns.SIZE_680}px`,
    },
    {
      name: <span className="fw-bold">AI REPLY</span>,
      selector: (row) => (
        <StatusIndicator
          isActive={isFeatureActive(row?.brandFeatures, featureFlagsEnums.AI_REPLY)}
          className="px-0"
        />
      ),
      width: `${tableColumns.SIZE_100}px`,
    },
    {
      name: <span className="fw-bold">INBOX</span>,
      selector: (row) => (
        <StatusIndicator
          isActive={isFeatureActive(row?.brandFeatures, featureFlagsEnums.INBOX)}
          className="px-0"
        />
      ),
      width: `${tableColumns.SIZE_100}px`,
    },
    {
      name: <span className="fw-bold">NPS-FIRST</span>,
      selector: (row) => (
        <StatusIndicator
          isActive={isFeatureActive(
            row?.brandFeatures,
            featureFlagsEnums.NPS_FIRST
          )}
          className="px-0"
        />
      ),
      width: `${tableColumns.SIZE_100}px`,
    },
    {
      name: <span className="fw-bold">IN-STORE</span>,
      selector: (row) => (
        <StatusIndicator
          isActive={isFeatureActive(
            row?.brandFeatures,
            featureFlagsEnums.IN_STORE
          )}
          className="px-0"
        />
      ),
      width: `${tableColumns.SIZE_100}px`,
    },
    {
      name: <span className="fw-bold">ACTION</span>,
      width: `${tableColumns.SIZE_100}px`,
      selector: FeatureFlagActionComponent,
    },
  ];
};

export const aiReplyColumns = (
  AIReplyActionComponent,
  SocialMediaLinkComponent
) => {
  return [
    columnHelper.accessor("brandName", {
      header: "BRAND",
      size: tableColumns.SIZE_300,
      enableHiding: false,
    }),
    columnHelper.accessor("facebookLink", {
      header: () => <span className="ps-2">FACEBOOK LINK</span>,
      enableSorting: false,
      size: tableColumns.SIZE_200,
      cell: SocialMediaLinkComponent,
    }),
    columnHelper.accessor("instagramLink", {
      header: () => <span className="ps-2">INSTAGRAM LINK</span>,
      enableSorting: false,
      size: tableColumns.SIZE_200,
      cell: SocialMediaLinkComponent,
    }),
    columnHelper.accessor("brandWebsiteLink", {
      header: () => <span className="ps-2">BRAND WEBSITE</span>,
      enableSorting: false,
      size: tableColumns.SIZE_200,
      cell: SocialMediaLinkComponent,
    }),
    columnHelper.accessor("action", {
      header: "ACTION",
      enableSorting: false,
      size: tableColumns.SIZE_60,
      cell: AIReplyActionComponent,
    }),
  ];
};

export const transformBrandData = (brands) => {
  return brands.map((brand) => {
    const socialLinks = brand.brandSocialLinks || [];

    const facebookLink =
      socialLinks.find(
        (link) => link.platform === socialLinkPlatformEnum.Facebook
      )?.url || emptyString;

    const instagramLink =
      socialLinks.find(
        (link) => link.platform === socialLinkPlatformEnum.Instagram
      )?.url || emptyString;

    const brandWebsiteLink =
      socialLinks.find(
        (link) => link.platform === socialLinkPlatformEnum.BrandWebsite
      )?.url || emptyString;

    return {
      facebookLink,
      instagramLink,
      brandWebsiteLink,
      brandId: brand.brandId,
      brandName: brand.brandName,
    };
  });
};
